<script setup lang="ts">
import { router } from '@inertiajs/vue3';
import { Languages } from 'lucide-vue-next';
import { onMounted, ref, computed } from 'vue'; // Ajout de ref et computed
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// 1. On utilise une ref simple pour le serveur, qui sera liée au localStorage au montage
const locale = ref('fr');

const languages = [
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
];

const setLanguage = (code: string) => {
    locale.value = code;

    if (typeof window !== 'undefined') {
        // Mise à jour du localStorage manuelle ou via une ref persistante après montage
        localStorage.setItem('locale', code);
        document.cookie = `app_locale=${code}; path=/; max-age=31536000; SameSite=Lax`;

        // On recharge la page pour que le backend prenne en compte la nouvelle locale
        router.reload();
    }
};

onMounted(() => {
    // 2. On récupère la valeur du localStorage UNIQUEMENT sur le client
    const savedLocale = localStorage.getItem('locale');

    if (savedLocale) {
        locale.value = savedLocale;
    }

    // Initialiser le cookie si absent
    if (!document.cookie.includes('app_locale')) {
        setLanguage(locale.value);
    }
});

const currentLanguage = computed(
    () => languages.find((l) => l.code === locale.value) || languages[0],
);
</script>

<template>
    <DropdownMenu>
        <DropdownMenuTrigger as-child>
            <Button variant="ghost" class="flex items-center gap-2 px-3">
                <Languages class="h-4 w-4" />
                <span class="text-sm font-medium">{{
                    currentLanguage.flag
                }}</span>
                <span class="sr-only"
                    >Langue actuelle : {{ currentLanguage.name }}</span
                >
            </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
            <DropdownMenuItem
                v-for="lang in languages"
                :key="lang.code"
                @click="setLanguage(lang.code)"
                :class="{ 'bg-accent': locale === lang.code }"
            >
                <span class="mr-2">{{ lang.flag }}</span>
                {{ lang.name }}
            </DropdownMenuItem>
        </DropdownMenuContent>
    </DropdownMenu>
</template>
